import React from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import stylizr from '@aftonbladet/stylizr';

import type { HTMLProps } from 'react';
import type {
  Markup,
  Text,
  ListBlock,
  TextBlock,
  RichTextBlock as RichTextBlockContent,
} from '../../../data-sources/content-components/types.js';

import {
  MarkupOptions,
  ListBlockTypes,
  TextBlockTypes,
} from '../../../data-sources/content-components/types.js';

interface MarkUpWithOptionalHTMLProps extends Markup {
  attributes?: Record<keyof HTMLProps<HTMLElement>, string>;
}

interface RichTextExtended extends Text {
  markup: MarkUpWithOptionalHTMLProps[];
}

function hasAttributes(
  data: Markup | MarkUpWithOptionalHTMLProps,
): data is MarkUpWithOptionalHTMLProps {
  return 'attributes' in data && data.attributes !== undefined;
}

const stylizrInstance = stylizr({
  [MarkupOptions.Bold]: (text) => <strong>{text}</strong>,
  [MarkupOptions.Italic]: (text) => <em>{text}</em>,
  [MarkupOptions.SuperScript]: (text) => <sup>{text}</sup>,
  [MarkupOptions.SubScript]: (text) => <sub>{text}</sub>,
  [MarkupOptions.Highlight]: (text) => <mark>{text}</mark>,
  [MarkupOptions.Link]: (text, _, data) => {
    const additionalAttributes = hasAttributes(data) ? data.attributes : {};

    return (
      <a className="link" href={data.uri} {...additionalAttributes}>
        {text}
      </a>
    );
  },
  [MarkupOptions.LinkInternal]: (text, _, data) =>
    data.uri ? (
      <a className="link" href={data.uri.split(':').pop()}>
        {text}
      </a>
    ) : (
      ''
    ),
} as Record<
  string,
  (
    text: string,
    key: string,
    data: Markup | MarkUpWithOptionalHTMLProps,
  ) => unknown
>);

interface RichTextElementProps {
  content: Text | RichTextExtended;
}

export const RichTextElement: React.FC<RichTextElementProps> = ({
  content: richText,
}) => {
  return stylizrInstance(richText.value, richText.markup);
};

interface RichTextListBlockProps {
  content: ListBlock;
}

export const RichTextListBlock: React.FC<RichTextListBlockProps> = ({
  content: listBlock,
}) => {
  const listItems = listBlock.items.map((text, i) => {
    return (
      <li key={i}>
        <RichTextElement content={text} />
      </li>
    );
  });

  switch (listBlock.blockType) {
    case ListBlockTypes.Unordered:
    case ListBlockTypes.Checked:
      return <ul>{listItems}</ul>;
    case ListBlockTypes.Ordered:
      return <ol>{listItems}</ol>;
  }
};

interface RichTextBlockProps {
  content: RichTextBlockContent;
}

export const RichTextBlock: React.FC<RichTextBlockProps> = ({ content }) => {
  if ((content as TextBlock).text) {
    const textBlock = content as TextBlock;

    switch (textBlock.blockType) {
      case TextBlockTypes.Heading:
        return (
          <h1>
            <RichTextElement content={textBlock.text} />
          </h1>
        );
      case TextBlockTypes.Heading2:
        return (
          <h2>
            <RichTextElement content={textBlock.text} />
          </h2>
        );
      case TextBlockTypes.Paragraph:
        if (textBlock.text.value === '') {
          return <br />;
        }

        return (
          <p>
            <RichTextElement content={textBlock.text} />
          </p>
        );
      case TextBlockTypes.Quote:
        return (
          <p className="quote">
            <RichTextElement content={textBlock.text} />
          </p>
        );
    }
  } else if ((content as ListBlock).items) {
    return <RichTextListBlock content={content as ListBlock} />;
  }

  throw new Error('Unknown block type!');
};

interface RichTextSectionProps {
  content: RichTextBlockContent[];
}

export const RichTextSection: React.FC<RichTextSectionProps> = ({
  content,
}) => {
  return (
    <>
      {content.map((block, i) => (
        <RichTextBlock content={block} key={i} />
      ))}
    </>
  );
};

/**
 * Utility adapter class for converting rich text to plain text.
 */
export class PlainTextAdapter {
  static richTextElement(content: Text | RichTextExtended): string {
    return content.value;
  }

  static richTextListBlock(content: ListBlock): string {
    const listItems = content.items.map((item, i) => {
      let sigil: string;

      switch (content.blockType) {
        case ListBlockTypes.Checked:
        case ListBlockTypes.Unordered:
          sigil = '-';
          break;
        case ListBlockTypes.Ordered:
          sigil = `${i}.`;
          break;
      }

      return `  ${sigil} ${PlainTextAdapter.richTextElement(item)}`;
    });

    return listItems.join('\n');
  }

  static richTextBlock(content: RichTextBlockContent): string {
    if ((content as ListBlock).items) {
      return PlainTextAdapter.richTextListBlock(content as ListBlock);
    } else if ((content as TextBlock).text) {
      return PlainTextAdapter.richTextElement((content as TextBlock).text);
    }

    throw new Error('Unreachable!');
  }

  static richTextSection(content: RichTextBlockContent[]): string {
    const ret = content.map((block) => {
      return PlainTextAdapter.richTextBlock(block);
    });

    return ret.join('\n\n');
  }
}
